import { Link } from "react-router-dom";
import { INDIAN_STATES, UNION_TERRITORIES } from "@/data/states";

const Field = ({ id, label, error, optional, children }) => (
  <div>
    <label htmlFor={id} className="field-label">
      {label} {optional && <span className="font-normal text-slate-600">(optional)</span>}
    </label>
    {children}
    {error && <p id={`${id}-err`} className="field-error" role="alert">{error}</p>}
  </div>
);

const FIELDS = [
  { k: "name", label: "Full name", auto: "name", span: true },
  { k: "phone", label: "Mobile number (10 digits)", auto: "tel-national", type: "tel", mode: "numeric", max: 14 },
  { k: "email", label: "Email", auto: "email", type: "email" },
  { k: "line1", label: "Address line 1", auto: "address-line1", span: true, placeholder: "House / flat no., street" },
  { k: "line2", label: "Address line 2", auto: "address-line2", span: true, optional: true, placeholder: "Area, landmark" },
  { k: "city", label: "City", auto: "address-level2" },
];

export const CheckoutForm = ({ form, setField, errors, agree, setAgree }) => (
  <div className="grid gap-5 sm:grid-cols-2" data-testid="checkout-form">
    {FIELDS.map((f) => (
      <div key={f.k} className={f.span ? "sm:col-span-2" : ""}>
        <Field id={`co-${f.k}`} label={f.label} error={errors[f.k]} optional={f.optional}>
          <input
            id={`co-${f.k}`}
            type={f.type || "text"}
            inputMode={f.mode}
            maxLength={f.max || 200}
            autoComplete={f.auto}
            placeholder={f.placeholder}
            value={form[f.k]}
            onChange={(e) => setField(f.k, e.target.value)}
            aria-invalid={!!errors[f.k]}
            aria-describedby={errors[f.k] ? `co-${f.k}-err` : undefined}
            className="field-input"
            data-testid={`checkout-${f.k}-input`}
          />
        </Field>
      </div>
    ))}
    <Field id="co-state" label="State / Union territory" error={errors.state}>
      <select id="co-state" value={form.state} onChange={(e) => setField("state", e.target.value)} autoComplete="address-level1" aria-invalid={!!errors.state} className="field-input" data-testid="checkout-state-select">
        <option value="">Select…</option>
        <optgroup label="States">{INDIAN_STATES.map((s) => <option key={s}>{s}</option>)}</optgroup>
        <optgroup label="Union territories">{UNION_TERRITORIES.map((s) => <option key={s}>{s}</option>)}</optgroup>
      </select>
    </Field>
    <Field id="co-pincode" label="PIN code (6 digits)" error={errors.pincode}>
      <input id="co-pincode" inputMode="numeric" maxLength={6} autoComplete="postal-code" value={form.pincode} onChange={(e) => setField("pincode", e.target.value.replace(/\D/g, ""))} aria-invalid={!!errors.pincode} className="field-input" data-testid="checkout-pincode-input" />
    </Field>
    <p className="text-sm text-slate-600 sm:col-span-2">We deliver within India only. Country: India.</p>
    <div className="sm:col-span-2">
      <label className="flex items-start gap-3 text-base text-slate-700">
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-brand-greenDark" aria-invalid={!!errors.agree} data-testid="checkout-agree-checkbox" />
        <span>
          I agree to the <Link to="/terms-and-conditions" target="_blank" className="font-semibold text-brand-blue underline">Terms &amp; Conditions</Link> and the{" "}
          <Link to="/refund-and-cancellation-policy" target="_blank" className="font-semibold text-brand-blue underline">Refund &amp; Cancellation Policy</Link>.
        </span>
      </label>
      {errors.agree && <p className="field-error" role="alert">{errors.agree}</p>}
    </div>
  </div>
);

export const validateCheckout = (f, agree) => {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Enter your full name";
  if (!/^[6-9]\d{9}$/.test(f.phone.replace(/\D/g, "").replace(/^91(?=\d{10}$)/, ""))) e.phone = "Enter a valid 10-digit mobile number";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Enter a valid email address";
  if (f.line1.trim().length < 3) e.line1 = "Enter your house / flat no. and street";
  if (f.city.trim().length < 2) e.city = "Enter your city";
  if (!f.state) e.state = "Select your state or union territory";
  if (!/^[1-9]\d{5}$/.test(f.pincode)) e.pincode = "Enter a valid 6-digit PIN code";
  if (!agree) e.agree = "Please agree to the Terms and the Refund & Cancellation Policy";
  return e;
};
